import React, { useEffect } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';
import { updatePageSEO, generateStructuredData } from '../../utils/seo';

const RecruitPage: React.FC = () => {
  useEffect(() => {
    const siteUrl = window.location.origin;
    updatePageSEO({
      title: '求人情報｜おかやま司法書士事務所 - 岡山市・倉敷市で司法書士・補助者募集',
      description: '岡山市・倉敷市のおかやま司法書士事務所で司法書士・司法書士補助者を募集。月給22万円〜50万円、各種社会保険完備、資格取得支援制度あり。ワークライフバランス重視の職場環境で一緒に成長しませんか。',
      keywords: '求人,司法書士募集,司法書士補助者,転職,岡山市,倉敷市,法務事務所,資格取得支援',
      canonical: `${siteUrl}/recruit`,
      ogTitle: '求人情報｜おかやま司法書士事務所',
      ogDescription: '岡山市・倉敷市のおかやま司法書士事務所で司法書士・司法書士補助者を募集。充実した研修制度とワークライフバランス重視の職場環境。',
      ogUrl: `${siteUrl}/recruit`,
      structuredData: generateStructuredData.recruit()
    });
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-r from-green-600 to-green-800">
          <div className="absolute inset-0">
            <img 
              src="https://public.readdy.ai/ai/img_res/df8b2d5b3f28cb831de1c636c4967f76.jpg"
              alt="チームワーク"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-green-900/70"></div>
          </div>
          <div className="relative z-10 container mx-auto px-4 text-center">
            <div className="flex justify-center mb-6 overflow-x-auto pb-2">
              <div className="flex space-x-2 px-4 min-w-max">
                <a href="/" className="px-3 md:px-4 py-2 bg-white/20 text-white rounded-full hover:bg-white/30 transition-colors whitespace-nowrap text-xs md:text-sm">
                  ホーム
                </a>
                <a href="/about" className="px-3 md:px-4 py-2 bg-white/20 text-white rounded-full hover:bg-white/30 transition-colors whitespace-nowrap text-xs md:text-sm">
                  会社概要
                </a>
                <a href="/recruit" className="px-3 md:px-4 py-2 bg-white text-green-600 rounded-full whitespace-nowrap text-xs md:text-sm">
                  求人情報
                </a>
                <a href="/blog" className="px-3 md:px-4 py-2 bg-white/20 text-white rounded-full hover:bg-white/30 transition-colors whitespace-nowrap text-xs md:text-sm">
                  ブログ
                </a>
              </div>
            </div>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6">求人情報</h1>
            <p className="text-base md:text-xl text-gray-200 max-w-3xl mx-auto px-4">
              一緒に成長し、地域社会に貢献する仲間を募集しています
            </p>
          </div>
        </section>

        {/* Message from Representative */}
        <section className="py-12 md:py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center max-w-6xl mx-auto">
              <div>
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20Japanese%20male%20business%20leader%20in%20his%2050s%2C%20confident%20and%20approachable%20expression%2C%20modern%20office%20background%2C%20natural%20lighting%2C%20wearing%20business%20suit%2C%20leadership%20presence%2C%20no%20text%20visible&width=500&height=600&seq=recruit-ceo&orientation=portrait"
                  alt="代表からのメッセージ"
                  className="w-full h-80 md:h-96 object-cover object-top rounded-lg shadow-lg"
                />
              </div>
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 md:mb-6">代表からのメッセージ</h2>
                <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-4 md:mb-6">
                  おかやま司法書士事務所では、法的サービスを通じて地域社会に貢献する、意欲的な仲間を募集しています。
                </p>
                <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-4 md:mb-6">
                  当事務所では、一人ひとりの成長を大切にし、専門知識の習得から実務経験まで、充実した研修制度を整えています。また、ワークライフバランスを重視し、働きやすい環境づくりに努めています。
                </p>
                <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                  法律の専門家として、お客様の人生に寄り添い、社会に貢献したいという志を持った方のご応募をお待ちしています。
                </p>
                <div className="mt-6 md:mt-8">
                  <p className="text-base md:text-lg font-semibold text-gray-900">代表司法書士</p>
                  <p className="text-xl md:text-2xl font-bold text-green-600">岡山 太郎</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Job Openings */}
        <section className="py-12 md:py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-8 md:mb-12">募集職種</h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 max-w-6xl mx-auto">
              {/* 司法書士 */}
              <div className="bg-white rounded-lg shadow-lg p-6 md:p-8">
                <div className="flex items-center mb-4 md:mb-6">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-green-100 rounded-lg flex items-center justify-center mr-3 md:mr-4 flex-shrink-0">
                    <i className="ri-scales-line text-xl md:text-2xl text-green-600"></i>
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900">司法書士</h3>
                </div>
                
                <div className="space-y-3 md:space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm md:text-base">業務内容</h4>
                    <ul className="text-gray-700 space-y-1 text-xs md:text-sm">
                      <li>• 不動産登記業務</li>
                      <li>• 商業登記業務</li>
                      <li>• 相続手続き</li>
                      <li>• 債務整理業務</li>
                      <li>• 成年後見業務</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm md:text-base">応募資格</h4>
                    <ul className="text-gray-700 space-y-1 text-xs md:text-sm">
                      <li>• 司法書士資格保有者</li>
                      <li>• 実務経験3年以上優遇</li>
                      <li>• 普通自動車免許</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm md:text-base">待遇</h4>
                    <ul className="text-gray-700 space-y-1 text-xs md:text-sm">
                      <li>• 月給：350,000円〜500,000円</li>
                      <li>• 賞与年2回（業績による）</li>
                      <li>• 各種社会保険完備</li>
                      <li>• 退職金制度あり</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* 司法書士補助者 */}
              <div className="bg-white rounded-lg shadow-lg p-6 md:p-8">
                <div className="flex items-center mb-4 md:mb-6">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-3 md:mr-4 flex-shrink-0">
                    <i className="ri-file-text-line text-xl md:text-2xl text-blue-600"></i>
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900">司法書士補助者</h3>
                </div>
                
                <div className="space-y-3 md:space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm md:text-base">業務内容</h4>
                    <ul className="text-gray-700 space-y-1 text-xs md:text-sm">
                      <li>• 登記申請書類の作成補助</li>
                      <li>• 法務局での調査業務</li>
                      <li>• お客様対応</li>
                      <li>• 事務処理全般</li>
                      <li>• 司法書士のサポート業務</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm md:text-base">応募資格</h4>
                    <ul className="text-gray-700 space-y-1 text-xs md:text-sm">
                      <li>• 高校卒業以上</li>
                      <li>• 司法書士試験受験予定者歓迎</li>
                      <li>• PCスキル（Word、Excel）</li>
                      <li>• 普通自動車免許</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm md:text-base">待遇</h4>
                    <ul className="text-gray-700 space-y-1 text-xs md:text-sm">
                      <li>• 月給：220,000円〜300,000円</li>
                      <li>• 賞与年2回</li>
                      <li>• 各種社会保険完備</li>
                      <li>• 資格取得支援制度あり</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Work Environment */}
        <section className="py-12 md:py-20 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-8 md:mb-12">働く環境</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto">
              <div className="text-center">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
                  <i className="ri-time-line text-2xl md:text-3xl text-green-600"></i>
                </div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 md:mb-4">ワークライフバランス</h3>
                <p className="text-sm md:text-base text-gray-700 px-2">
                  残業時間の削減に取り組み、プライベートの時間も大切にできる職場環境を整えています。
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
                  <i className="ri-graduation-cap-line text-2xl md:text-3xl text-blue-600"></i>
                </div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 md:mb-4">充実した研修制度</h3>
                <p className="text-sm md:text-base text-gray-700 px-2">
                  新人研修から専門研修まで、段階的にスキルアップできる研修プログラムを用意しています。
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
                  <i className="ri-team-line text-2xl md:text-3xl text-purple-600"></i>
                </div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 md:mb-4">チームワーク</h3>
                <p className="text-sm md:text-base text-gray-700 px-2">
                  お互いを尊重し、協力し合える温かい職場環境で、一緒に成長していけます。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-12 md:py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-8 md:mb-12">福利厚生</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 max-w-6xl mx-auto">
              <div className="bg-white rounded-lg p-5 md:p-6 text-center shadow-lg">
                <i className="ri-shield-check-line text-2xl md:text-3xl text-green-600 mb-3 md:mb-4"></i>
                <h3 className="font-semibold text-gray-900 mb-2 text-sm md:text-base">社会保険完備</h3>
                <p className="text-xs md:text-sm text-gray-600">健康保険・厚生年金・雇用保険・労災保険</p>
              </div>
              
              <div className="bg-white rounded-lg p-5 md:p-6 text-center shadow-lg">
                <i className="ri-money-dollar-circle-line text-2xl md:text-3xl text-blue-600 mb-3 md:mb-4"></i>
                <h3 className="font-semibold text-gray-900 mb-2 text-sm md:text-base">退職金制度</h3>
                <p className="text-xs md:text-sm text-gray-600">勤続年数に応じた退職金制度</p>
              </div>
              
              <div className="bg-white rounded-lg p-5 md:p-6 text-center shadow-lg">
                <i className="ri-book-open-line text-2xl md:text-3xl text-purple-600 mb-3 md:mb-4"></i>
                <h3 className="font-semibold text-gray-900 mb-2 text-sm md:text-base">資格取得支援</h3>
                <p className="text-xs md:text-sm text-gray-600">司法書士試験等の資格取得を全面サポート</p>
              </div>
              
              <div className="bg-white rounded-lg p-5 md:p-6 text-center shadow-lg">
                <i className="ri-car-line text-2xl md:text-3xl text-orange-600 mb-3 md:mb-4"></i>
                <h3 className="font-semibold text-gray-900 mb-2 text-sm md:text-base">駐車場完備</h3>
                <p className="text-xs md:text-sm text-gray-600">専用駐車場を無料でご利用いただけます</p>
              </div>
            </div>
          </div>
        </section>

        {/* Application Process */}
        <section className="py-12 md:py-20 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-8 md:mb-12">応募の流れ</h2>
            
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                <div className="text-center">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-green-600 text-white rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4 text-xl md:text-2xl font-bold">
                    1
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-sm md:text-base">書類選考</h3>
                  <p className="text-xs md:text-sm text-gray-600 px-2">履歴書・職務経歴書による書類選考</p>
                </div>
                
                <div className="text-center">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-green-600 text-white rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4 text-xl md:text-2xl font-bold">
                    2
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-sm md:text-base">一次面接</h3>
                  <p className="text-xs md:text-sm text-gray-600 px-2">人事担当者との面接</p>
                </div>
                
                <div className="text-center">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-green-600 text-white rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4 text-xl md:text-2xl font-bold">
                    3
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-sm md:text-base">最終面接</h3>
                  <p className="text-xs md:text-sm text-gray-600 px-2">代表との最終面接</p>
                </div>
                
                <div className="text-center">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-green-600 text-white rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4 text-xl md:text-2xl font-bold">
                    4
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-sm md:text-base">内定・入社</h3>
                  <p className="text-xs md:text-sm text-gray-600 px-2">条件面談後、入社日決定</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact for Recruitment */}
        <section className="py-12 md:py-20 bg-green-50">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 md:mb-6">採用に関するお問い合わせ</h2>
            <p className="text-base md:text-xl text-gray-600 mb-6 md:mb-8 max-w-3xl mx-auto px-4">
              ご質問やご相談がございましたら、お気軽にお問い合わせください。まずは話を聞いてみたいという方も大歓迎です。
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center max-w-2xl mx-auto">
              <a 
                href="tel:086-012-3456" 
                className="inline-flex items-center justify-center px-6 md:px-8 py-3 md:py-4 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap text-sm md:text-base"
              >
                <i className="ri-phone-line mr-2"></i>
                086-012-3456
              </a>
              <a 
                href="#contact" 
                className="inline-flex items-center justify-center px-6 md:px-8 py-3 md:py-4 border-2 border-green-600 text-green-600 font-medium rounded-lg hover:bg-green-600 hover:text-white transition-colors whitespace-nowrap text-sm md:text-base"
              >
                <i className="ri-mail-line mr-2"></i>
                メールでお問い合わせ
              </a>
            </div>
            
            <p className="text-xs md:text-sm text-gray-500 mt-4 md:mt-6">
              受付時間：平日 9:00〜18:00（土日祝日は事前予約制）
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default RecruitPage;
